import { useState, useEffect } from 'react';
import { observer, inject } from 'mobx-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';

import Table from '../../components/table/Table';
import useLanguageChange from '../../utils/hooks/useLanguageChange';
import '../credit-card-transactions/credit-card-transactions.scss';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAutoRefresh, useStatementData } from './hooks';
import DropdownMenu from '../../components/dropdown-menu/dropdown-menu';
import UncategorizedExpensesTable from './uncategorized-expenses-table/uncategorized-expenses-table';
import Toggle from '../../components/toggle/toggle';
import StatementImportsSection from './StatementImportsSection';

const columnNameKeys = {
  card_label: 'cc_statements.column_names.card_name',
  status: 'cc_statements.column_names.status',
  period: 'cc_statements.column_names.period',
  year: 'cc_statements.column_names.year',
  total_with_tax: 'cc_statements.column_names.total_with_tax',
  start_date: 'cc_statements.column_names.start_date',
  end_date: 'cc_statements.column_names.end_date',
  paired_transactions: 'cc_statements.column_names.paired_transactions',
  unmatched_expenses: 'cc_statements.column_names.unmatched_expenses',
  currency_code: 'cc_statements.column_names.currency_code',
};

const AdminColumnNameKeys = {
  account_mask: 'cc_statements.column_names.card_number',
  card_type: 'cc_statements.column_names.card_type',
  ...columnNameKeys,
};

export const adminRoles = ['Admin', 'Manager', 'SuperAdmin'];

const FILTER_STORAGE_KEY = 'creditCardStatementsFilter';
const defaultFilters = { accountMask: 'all', year: 'all', month: 'all' };

const loadFiltersFromStorage = () => {
  try {
    const raw = localStorage.getItem(FILTER_STORAGE_KEY);
    if (!raw) return defaultFilters;
    const parsed = JSON.parse(raw);
    return { ...defaultFilters, ...parsed };
  } catch {
    return defaultFilters;
  }
};

const persistFilters = (filters) => {
  localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
};

const CreditCardTransactions = ({ data }) => {
  useLanguageChange(data.t);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const userCanSeeAdminData = adminRoles.includes(data.role || '');
  const [accountMasks, setAccountMasks] = useState([]);
  const [storedFilters] = useState(() => loadFiltersFromStorage());
  const [selectedAccountMask, setSelectedAccountMask] = useState(searchParams.get('accountMask') || storedFilters.accountMask);
  const [selectedYear, setSelectedYear] = useState(storedFilters.year);
  const [selectedMonth, setSelectedMonth] = useState(storedFilters.month);
  const [hideCompletedStatements, setHideCompletedStatements] = useState(false);
  const { refreshing, refreshData } = useAutoRefresh();
  const { statements, fetchStatements, loading: statementsLoading, years } = useStatementData({ t: data.t, setAccountMasks });
  
  const months = [
      {value: 1, label: data.t.read('month.January')},
      {value: 2, label: data.t.read('month.February')},
      {value: 3, label: data.t.read('month.March')},
      {value: 4, label: data.t.read('month.April')},
      {value: 5, label: data.t.read('month.May')},
      {value: 6, label: data.t.read('month.June')},
      {value: 7, label: data.t.read('month.July')},
      {value: 8, label: data.t.read('month.August')},
      {value: 9, label: data.t.read('month.September')},
      {value: 10, label: data.t.read('month.October')},
      {value: 11, label: data.t.read('month.November')},
      {value: 12, label: data.t.read('month.December')}
  ]
  useEffect(() => {
    const accountMaskFilter = selectedAccountMask && selectedAccountMask !== 'all' ? selectedAccountMask : '';

    const yearFilter = selectedYear && selectedYear !== 'all' ? selectedYear : '';
    
    const monthFilter = selectedMonth && selectedMonth !== 'all' ? selectedMonth : '';
    
    const sorting = [{ id: 'start_date', desc: true }];
    let filters = [];
    if (accountMaskFilter) {
      filters.push({ id: 'account_mask', value: accountMaskFilter });
    }
    if (yearFilter) {
      filters.push({ id: 'year', value: Number(yearFilter) });
    }
    if (monthFilter) {
        filters.push({ id: 'period', value: Number(monthFilter) });
    }
    
    fetchStatements({ sorting, filters, hideCompletedStatements });
  }, [selectedYear, selectedAccountMask, selectedMonth, hideCompletedStatements, data.t, data.role, data.origin_role]);

  useEffect(() => {
    persistFilters({
      accountMask: selectedAccountMask,
      year: selectedYear,
      month: selectedMonth,
    });
  }, [selectedAccountMask, selectedYear, selectedMonth]);

  const handleRowClick = async (row) => {
    // Pass the account mask filter to the transaction page if one is selected
    if (selectedAccountMask && selectedAccountMask !== 'all') {
      navigate(`/c_card/statement/${row._id}?accountMask=${selectedAccountMask}`);
    } else {
      navigate(`/c_card/statement/${row._id}`);
    }
  };

  const handleAccountMaskChange = (e) => {
    const newAccountMask = e.target.value;
    setSelectedAccountMask(newAccountMask);
    
    // Update URL params
    const newSearchParams = new URLSearchParams(searchParams);
    if (newAccountMask === 'all') {
      newSearchParams.delete('accountMask');
    } else {
      newSearchParams.set('accountMask', newAccountMask);
    }
    setSearchParams(newSearchParams, { replace: true });
  };

  return (
    <div className='card p-1 mt-2 rounded-lg transaction-card'>
      <div className='tr-top-bar'>
        <h2 className='ml-4 mt-4'>
          <FontAwesomeIcon icon={faArrowLeft} className='mr-2 go-back' onClick={() => navigate('/dashboard')} />
          {data.t.read('cc_statements.title')}
        </h2>
        <div className='mr-2'>
          <DropdownMenu refreshing={refreshing}>
            <button
              className='dropdown-item d-flex align-items-center'
              onClick={async () => {
                refreshData().then((response) => {
                  if (response.data.message === 'ok') {
                    fetchStatements();
                  } else {
                    toast.error(data.t.read('cc_transactions.refresh_error'));
                  }
                });
              }}
            >
              <FontAwesomeIcon icon={faSync} className='mr-2' />
              {data.t.read('cc_transactions.refresh_data')}
            </button>
          </DropdownMenu>
        </div>
      </div>
      <div className='card-body'>
        <div className='filters-container'>
          <div className='account-mask-selector ml-0'>
            <label htmlFor='account_mask' className='form-label'>
              {data.t.read('cc_statements.column_names.card_number')}
            </label>
            <select className='form-control' id='account_mask' value={selectedAccountMask} onChange={handleAccountMaskChange}>
              <option value='all'>{data.t.read('cc_statements.all_cards')}</option>
              {accountMasks.map((mask) => (
                <option key={mask} value={mask}>
                  **** **** **** {mask}
                </option>
              ))}
            </select>
          </div>

          <div className='account-mask-selector ml-0'>
            <label htmlFor='account_mask' className='form-label'>
              {data.t.read('year')}
            </label>
            <select className='form-control' id='account_mask' value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}>
              <option value='all'>{data.t.read('cc_statements.all_years')}</option>
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

            <div className='account-mask-selector ml-0'>
                <label htmlFor='account_mask' className='form-label'>
                    {data.t.read('monthly')}
                </label>
                <select className='form-control' id='account_mask' value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
                    <option value='all'>{data.t.read('cc_statements.all_months')}</option>
                    {months.map((month) => (
                        <option key={month.value} value={month.value}>
                            {month.label}
                        </option>
                    ))}
                </select>
            </div>
            
          <div className='mb-2'>
            <Toggle
              id='show-incomplete-statements-toggle'
              isOn={hideCompletedStatements}
              handleToggle={() => setHideCompletedStatements(!hideCompletedStatements)}
              label={data.t.read('cc_statements.isolate_incomplete_statements')}
            />
          </div>
        </div>
        <div className='card-title d-flex justify-content-center'>
          <h2>{data.t.read('statements_account_list')}</h2>
        </div>
        <div className='table m-0 mb-5 p-0'>
          <Table
            columnNames={userCanSeeAdminData ? AdminColumnNameKeys : columnNameKeys}
            tableData={statements}
            searchable={false}
            onRowClick={handleRowClick}
            isLoading={statementsLoading}
          />
        </div>
        <StatementImportsSection data={data} />
        <div className='table m-0 p-0'>
          <UncategorizedExpensesTable filteredMask={selectedAccountMask} refreshStatementData={fetchStatements} />
        </div>
      </div>
    </div>
  );
};

export default inject('data')(observer(CreditCardTransactions));
